/**
 * KAUL_CHILD_THEME_SubscribeModal
 * Manages the shared newsletter subscription modal and its triggers.
 */
class KAUL_CHILD_THEME_SubscribeModal {
    constructor(root = document) {
        this.root = root;
        this.modal = this.root.querySelector('[data-subscribe-modal]');

        if (!(this.modal instanceof HTMLElement)) {
            return;
        }

        this.triggers = Array.from(this.root.querySelectorAll('[data-subscribe-modal-trigger]'));

        if (this.triggers.length === 0) {
            return;
        }

        this.activeTrigger = null;
        this.focusableSelector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(',');

        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);

        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeydown);
    }

    handleDocumentClick(event) {
        const trigger = event.target.closest?.('[data-subscribe-modal-trigger]');
        if (trigger instanceof HTMLElement) {
            event.preventDefault();
            this.open(trigger);
            return;
        }

        const close = event.target.closest?.('[data-subscribe-modal-close]');
        if (close instanceof HTMLElement && !this.modal.hidden) {
            event.preventDefault();
            this.close();
        }
    }

    handleDocumentKeydown(event) {
        if (this.modal.hidden) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = this.getFocusableElements();
        if (focusable.length === 0) {
            event.preventDefault();
            this.modal.focus();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }

    getFocusableElements() {
        return Array.from(this.modal.querySelectorAll(this.focusableSelector)).filter((element) => {
            if (!(element instanceof HTMLElement)) {
                return false;
            }

            const style = window.getComputedStyle(element);
            return style.visibility !== 'hidden' && style.display !== 'none';
        });
    }

    open(trigger) {
        this.activeTrigger = trigger instanceof HTMLElement ? trigger : null;
        this.modal.hidden = false;
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('is-subscribe-modal-open');

        window.requestAnimationFrame(() => {
            const focusable = this.getFocusableElements();
            const target = focusable.find((element) => element.matches?.('input, textarea, select, button')) || focusable[0] || this.modal;

            if (target instanceof HTMLElement) {
                target.focus();
            }
        });
    }

    close() {
        this.modal.hidden = true;
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-subscribe-modal-open');

        if (this.activeTrigger instanceof HTMLElement) {
            this.activeTrigger.focus();
        }

        this.activeTrigger = null;
    }
}

export default KAUL_CHILD_THEME_SubscribeModal;
